import { CloseOutlined } from "@ant-design/icons";
import type { InputRef } from "antd";
import { Button, Col, Form, Input, message, Modal, Popconfirm, Row, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { ContentBox } from "../../components/layout/content";
import { LoginedLayout } from "../../components/layout/loginLayout";
import { HeaderTitle } from "../../components/styled-components";
import { PlaySound } from "../../functions/function";
import { GET, POST } from "../../services";
import {
  SHIPPING_REVERT_CHECKED,
  SHIPPING_SEARCH,
  SHIPPING_UPDATE_CHECKED,
  SHIPPING_VALIDATE_UPDATE,
} from "../../services/path";

interface ShippingRecord {
  id: number;
  order_no: string;
  tracking_no: string;
  license_plate: string;
  logistic: string;
  shipping_status: string;
  created_at: string;
  updated_at: string;
  submit_at?: string;
}

const translateErrorMessage = (errMsg: string): string => {
  const msg = errMsg.toLowerCase();
  if (msg.includes("order not found in waiting status")) {
    return "ไม่พบข้อมูลออเดอร์ในสถานะรอดำเนินการ กรุณาตรวจสอบอีกครั้ง";
  }
  if (msg.includes("order not found")) {
    return "ไม่พบข้อมูลออเดอร์นี้ในระบบ กรุณาตรวจสอบบาร์โค้ดอีกครั้ง";
  }
  if (msg.includes("scan the order number instead of tracking number")) {
    return "สำหรับออเดอร์ส่งด่วน (ยกเว้น Express Delivery (SPX)) กรุณาสแกนด้วย 'หมายเลขออเดอร์' แทนหมายเลขพัสดุ";
  }
  if (msg.includes("scan the tracking number for this logistics type")) {
    return "สำหรับออเดอร์ปกติ (รวม Express Delivery (SPX)) กรุณาสแกนด้วย 'หมายเลขพัสดุ'";
  }
  if (msg.includes("duplicate") || msg.includes("already processed")) {
    return "พัสดุชิ้นนี้ถูกสแกนไปแล้ว (ข้อมูลซ้ำ)";
  }
  if (msg.includes("order is canceled") || msg.includes("cancelled")) {
    return "ออเดอร์นี้ถูกยกเลิกจากระบบแล้ว ไม่สามารถจัดส่งได้";
  }
  if (msg.includes("missing platform")) {
    return "ข้อมูลผิดพลาด: ออเดอร์นี้ไม่มีการระบุแพลตฟอร์ม (Shopee/Lazada/TikTok)";
  }
  if (msg.includes("missing brand")) {
    return "ข้อมูลผิดพลาด: ออเดอร์นี้ไม่มีการระบุชื่อร้านค้า (Brand)";
  }
  if (
    msg.includes("failed to validate order status") ||
    msg.includes("api client not available") ||
    msg.includes("api client not initialized")
  ) {
    return "ไม่สามารถตรวจสอบสถานะกับทางระบบแพลตฟอร์มได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง";
  }
  if (msg.includes("only 'checked' orders can be reverted")) {
    return "ไม่สามารถยกเลิกได้ (สามารถยกเลิกได้เฉพาะออเดอร์ที่มีสถานะ Checked เท่านั้น)";
  }
  if (msg.includes("no 'checked' shipping records were found")) {
    return "ไม่พบออเดอร์สถานะ Checked ที่สามารถบันทึกทะเบียนรถได้";
  }
  if (
    msg.includes("failed to record") ||
    msg.includes("failed to process") ||
    msg.includes("failed to update") ||
    msg.includes("no shipping records were updated")
  ) {
    return "เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง (Database Error)";
  }
  if (msg.includes("required' tag") || msg.includes("invalid request payload")) {
    return "ข้อมูลที่ส่งไปไม่สมบูรณ์ (กรุณาระบุข้อมูลทะเบียนรถ)";
  }
  if (msg.includes("unauthorized")) {
    return "เซสชั่นหมดอายุหรือไม่ได้รับอนุญาต กรุณาเข้าสู่ระบบใหม่";
  }
  if (msg.includes("failed to fetch")) {
    return "ดึงข้อมูลล้มเหลว กรุณารีเฟรชหน้าเว็บ";
  }
  return errMsg; // Fallback to original
};

const ShippingPage: React.FC = () => {
  const inputRef = useRef<InputRef>(null);
  const licensePlateRef = useRef<InputRef>(null);
  const [duplicateData, setDuplicateData] = useState<ShippingRecord[]>([]);
  const [cancelData, setCancelData] = useState<ShippingRecord[]>([]);
  const [waitingData, setWaitingData] = useState<ShippingRecord[]>([]);
  const [checkedData, setCheckedData] = useState<ShippingRecord[]>([]);

  const [trackingNo, setTrackingNo] = useState<string>("");
  const [licensePlate, setLicensePlate] = useState<string>("");
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [lastActionTrackingNo, setLastActionTrackingNo] = useState<string>("");
  const [lastActionId, setLastActionId] = useState<number | null>(null);

  const tableStyle: React.CSSProperties = {
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
    borderRadius: "8px",
    overflow: "hidden",
  };

  const fetchTables = async () => {
    try {
      const [dupRes, cancelRes, waitingRes, checkedRes] = await Promise.all([
        GET(SHIPPING_SEARCH("duplicate", 0, "updated_at", "desc", "today")),
        GET(SHIPPING_SEARCH("cancel_ship", 0, "updated_at", "desc", "today")),
        GET(SHIPPING_SEARCH("waiting_for_shipping", 0, "created_at", "asc")),
        GET(SHIPPING_SEARCH("checked", 0, "updated_at", "desc")),
      ]);

      setDuplicateData(dupRes?.result ?? []);
      setCancelData(cancelRes?.result ?? []);
      setWaitingData(waitingRes?.result ?? []);
      setCheckedData(checkedRes?.result ?? []);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch shipping data.");
    }
  };

  useEffect(() => {
    inputRef.current?.focus();
    window.scrollTo(0, 0);
    fetchTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isModalVisible) {
      const timer = setTimeout(() => licensePlateRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isModalVisible]);

  useEffect(() => {
    if (!lastActionTrackingNo) return;

    const allRecords = [...checkedData, ...duplicateData, ...cancelData, ...waitingData];
    const matchingRecords = allRecords.filter(
      (r) => r.tracking_no === lastActionTrackingNo || r.order_no === lastActionTrackingNo,
    );

    if (matchingRecords.length > 0) {
      // Find the one with the most recent updated_at timestamp
      const targetRecord = matchingRecords.reduce((prev, current) => {
        const prevTime = new Date(prev.updated_at || prev.created_at).getTime();
        const currentTime = new Date(current.updated_at || current.created_at).getTime();
        return prevTime > currentTime ? prev : current;
      });
      setLastActionId(targetRecord.id);
    } else {
      setLastActionId(null);
    }
  }, [checkedData, duplicateData, cancelData, waitingData, lastActionTrackingNo]);

  const handleScanTracking = async () => {
    if (!trackingNo) return;
    const currentTracking = trackingNo.trim();
    setIsScanning(true);
    try {
      const payload = {
        scan_input: currentTracking,
      };
      await POST(SHIPPING_VALIDATE_UPDATE, payload);
      // If success, PlaySound and refetch
      PlaySound("success");
      await fetchTables();
      setLastActionTrackingNo(currentTracking);
    } catch (error: any) {
      const errMessage =
        error?.data?.message || error?.message || "Error validating tracking number";

      const translatedMessage = translateErrorMessage(errMessage);

      if (errMessage.toLowerCase().includes("duplicate")) {
        PlaySound("duplicate");
      } else {
        PlaySound("error");
      }
      message.error(translatedMessage);
      await fetchTables(); // Refresh to show new dup/cancel if applicable
      setLastActionTrackingNo(currentTracking);
    } finally {
      setIsScanning(false);
      setTrackingNo("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleSubmitLicensePlate = async () => {
    if (!licensePlate) {
      message.warning("กรุณาระบุข้อมูลทะเบียนรถก่อนทำการบันทึก");
      return;
    }
    if (checkedData.length === 0) {
      message.warning("ไม่มีออเดอร์ที่ถูกตรวจสอบแล้วให้บันทึก");
      return;
    }

    try {
      const payload = {
        license_plate: licensePlate.trim(),
      };
      const res = await POST(SHIPPING_UPDATE_CHECKED, payload);
      message.success(`Successfully updated ${res.result.rows_updated} orders with license plate.`);
      setLicensePlate("");
      setIsModalVisible(false);
      await fetchTables();
    } catch (error: any) {
      console.error(error);
      const errMessage =
        error?.data?.message || error?.message || "Failed to update checked shippings.";
      message.error(translateErrorMessage(errMessage));
    }
  };

  const handleRevertChecked = async (trackingNo: string) => {
    try {
      await POST(SHIPPING_REVERT_CHECKED, { tracking_no: trackingNo });
      message.success(`ออเดอร์ ${trackingNo} ถูกย้ายกลับไปยัง Waiting เรียบร้อยแล้ว`);
      await fetchTables();
      setLastActionTrackingNo(trackingNo);
    } catch (error: any) {
      console.error(error);
      const errMessage =
        error?.data?.message || error?.message || "Failed to revert shipping status.";
      message.error(translateErrorMessage(errMessage));
    } finally {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const commonColumns: ColumnsType<ShippingRecord> = [
    { title: "Order No", dataIndex: "order_no", key: "order_no" },
    { title: "Tracking No", dataIndex: "tracking_no", key: "tracking_no" },
    { title: "Logistic", dataIndex: "logistic", key: "logistic" },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (val) => (val ? dayjs(val.replace("Z", "")).format("YYYY-MM-DD HH:mm") : "-"),
    },
  ];

  const checkedColumns: ColumnsType<ShippingRecord> = [
    ...commonColumns.slice(0, 3), // order_no, tracking_no, logistic
    {
      title: "Checked At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (val) => (val ? dayjs(val.replace("Z", "")).format("YYYY-MM-DD HH:mm") : "-"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Popconfirm
          title="ยืนยันการยกเลิก"
          description={`คุณต้องการย้าย ${record.tracking_no || record.order_no} กลับไปที่ Waiting ใช่หรือไม่?`}
          onConfirm={() => handleRevertChecked(record.tracking_no || record.order_no)}
          onOpenChange={(open) => {
            if (!open) setTimeout(() => inputRef.current?.focus(), 0);
          }}
          okText="ใช่, ย้ายกลับ"
          cancelText="ยกเลิก"
        >
          <Button danger type="text" size="small" icon={<CloseOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const getRowClassName = (record: ShippingRecord) => {
    if (lastActionId === null || record.id !== lastActionId) return "";

    if (record.shipping_status === "checked") return "highlight-row-green";
    if (record.shipping_status === "duplicate") return "highlight-row-yellow";
    if (record.shipping_status === "cancel_ship") return "highlight-row-red";
    if (record.shipping_status === "waiting_for_shipping") return "highlight-row-blue";

    return "";
  };

  return (
    <LoginedLayout>
      <style>{`
        .highlight-row-green > td { background-color: #d9f7be !important; }
        .highlight-row-yellow > td { background-color: #fffb8f !important; }
        .highlight-row-red > td { background-color: #ffccc7 !important; }
        .highlight-row-blue > td { background-color: #bae0ff !important; }
      `}</style>
      <ContentBox style={{ backgroundColor: "#b6eefc" }}>
        <HeaderTitle>
          <h2>Shipping Management</h2>
        </HeaderTitle>
        <div style={{ marginTop: "2%" }}>
          {/* Top Section: Tracking Number Input */}
          <Row gutter={[16, 16]} justify="center">
            <Col xs={24} sm={16} md={12} lg={8}>
              <Form.Item label="Tracking Number">
                <Input
                  ref={inputRef}
                  value={trackingNo}
                  onChange={(e) => setTrackingNo(e.target.value)}
                  onPressEnter={handleScanTracking}
                  placeholder="Scan Tracking Number..."
                  size="large"
                  disabled={isScanning}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Action Bar for Submit */}
          <Row gutter={[16, 16]} justify="end" style={{ marginBottom: "16px" }}>
            <Col>
              <Button
                type="primary"
                size="large"
                onClick={() => setIsModalVisible(true)}
                disabled={checkedData.length === 0}
              >
                Submit All Checked
              </Button>
            </Col>
          </Row>

          {/* Tables Section (Waiting and Checked Top) */}
          <Row gutter={[16, 16]}>
            {/* Waiting for Shipping */}
            <Col xs={24} lg={12}>
              <h3 style={{ marginTop: 0 }}>Waiting to Update ({waitingData.length})</h3>
              <Table
                dataSource={waitingData}
                columns={commonColumns}
                rowKey="id"
                rowClassName={getRowClassName}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100", "500"],
                }}
                style={tableStyle}
                scroll={{ x: "max-content" }}
                size="small"
              />
            </Col>
            {/* Checked Table */}
            <Col xs={24} lg={12}>
              <h3 style={{ marginTop: 0, color: "green" }}>
                Prepared / Checked ({checkedData.length})
              </h3>
              <Table
                dataSource={checkedData}
                columns={checkedColumns}
                rowKey="id"
                rowClassName={getRowClassName}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100", "500"],
                }}
                style={tableStyle}
                scroll={{ x: "max-content" }}
                size="small"
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: "24px" }}>
            {/* Duplicates */}
            <Col xs={24} lg={12}>
              <h3 style={{ color: "orange" }}>Duplicates</h3>
              <Table
                dataSource={duplicateData}
                columns={commonColumns}
                rowKey="id"
                rowClassName={getRowClassName}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100", "500"],
                }}
                style={tableStyle}
                scroll={{ x: "max-content" }}
                size="small"
              />
            </Col>
            {/* Cancels */}
            <Col xs={24} lg={12}>
              <h3 style={{ color: "red" }}>Cancelled</h3>
              <Table
                dataSource={cancelData}
                columns={commonColumns}
                rowKey="id"
                rowClassName={getRowClassName}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100", "500"],
                }}
                style={tableStyle}
                scroll={{ x: "max-content" }}
                size="small"
              />
            </Col>
          </Row>
        </div>
      </ContentBox>

      {/* Modal for License Plate Confirmation */}
      <Modal
        title="Confirm Shipping Update"
        open={isModalVisible}
        onOk={handleSubmitLicensePlate}
        onCancel={() => {
          setIsModalVisible(false);
          setLicensePlate("");
        }}
        afterClose={() => inputRef.current?.focus()}
        focusTriggerAfterClose={false}
        okText="Confirm"
        cancelText="Cancel"
      >
        <p>
          กรุณาระบุข้อมูลทะเบียนรถสำหรับออเดอร์ที่ถูกตรวจสอบแล้วจำนวน {checkedData.length} รายการ:
        </p>
        <Input
          ref={licensePlateRef}
          placeholder="License Plate"
          value={licensePlate}
          onChange={(e) => setLicensePlate(e.target.value)}
          size="large"
          onPressEnter={handleSubmitLicensePlate}
        />
      </Modal>
    </LoginedLayout>
  );
};

export default ShippingPage;
